import Axios from 'axios';
import { flatMap, isEqual } from 'lodash';
import { computed } from 'mobx';
import {
  applySnapshot,
  cast,
  flow,
  getEnv,
  getRoot,
  resolveIdentifier,
  types,
} from 'mobx-state-tree';

import Category from '../models/Category';
import StatefulStore from '../models/StatefulStore';
import RequestState, { RequestStateType } from '../types/RequestState';
import { createErrorModel } from '../util/error';

const flatMapWithChildren = (categories) => {
  return categories.length > 0
    ? flatMap(categories, (category) => [
        category,
        ...flatMapWithChildren(category.children),
      ])
    : [];
};

const CategoryStore = StatefulStore.named('CategoryStore')
  .props({
    categories: types.optional(types.array(Category), []),
    categoryStates: types.optional(types.map(RequestStateType), {}),
    activeCategory: types.maybeNull(types.reference(Category)),
    latestActiveCategory: types.maybeNull(types.reference(Category)),
  })
  .views((self) => {
    // Either a dedicated observer or a keepAlive computed property
    // is required for the result to be actually properly cached
    // instead of getting recalculated every time it is called.
    // A MST view getter doesn't do this automatically.
    const memoizedFlattenedCategories = computed(
      () => {
        return flatMapWithChildren(self.categories);
      },
      { keepAlive: true }
    );

    return {
      get flattenedCategories() {
        return memoizedFlattenedCategories.get();
      },
      isInActiveCategoryHierarchy(category) {
        return self.activeCategory
          ? self.activeCategory.hierarchy.indexOf(category) !== -1
          : false;
      },
    };
  })
  .actions((self) => {
    let source;
    const ifShoppingCenter = () =>
      getRoot(self).configStore.siteConfig.isShoppingCenter;
    const categoriesApi = () =>
      ifShoppingCenter() ? 'shopping-center/categories' : 'categories';

    const setCategoryStates = (id, state) => {
      if (self.categoryStates.get(id) !== state) {
        self.categoryStates.set(id, state);
      }
    };

    return {
      setup(categories) {
        if (!isEqual(cast(categories), self.categories)) {
          self.categories = cast(categories);
        }
        self.setLoading(false);
      },
      setSingleStateError: (id, error) => {
        self.lastError = createErrorModel(error);
        setCategoryStates(id, RequestState.ERROR);
      },
      loadCategory: flow(function* loadCategory(id) {
        if (source) {
          source.cancel();
          source = null;
        }
        source = Axios.CancelToken.source();
        setCategoryStates(id, RequestState.LOADING);
        try {
          const category = yield getEnv(self).apiWrapper.request(
            `${categoriesApi()}/${id}`,
            { cancelToken: source.token }
          );

          const categoryModel = self.findCategoryById(category.id);
          if (categoryModel) {
            category.children = categoryModel.children;
            applySnapshot(categoryModel, category);
          }

          setCategoryStates(id, RequestState.LOADED);
        } catch (e) {
          self.setSingleStateError(id, e);
          throw e;
        }
      }),
      findCategoryById(categoryId) {
        return resolveIdentifier(Category, self.categories, categoryId);
      },
      setActiveCategory(activeCategoryId) {
        if (activeCategoryId) {
          self.activeCategory = resolveIdentifier(
            Category,
            getRoot(self),
            activeCategoryId
          );

          if (self.activeCategory) {
            self.latestActiveCategory = self.activeCategory;
          }

          if (!self.categoryStates.get(activeCategoryId)) {
            self.categoryStates.set(activeCategoryId, RequestState.LOADED);
          }
        } else {
          self.activeCategory = null;
        }
      },
    };
  });

export default CategoryStore;
